// Wraps fetch with a hard timeout. The browser will otherwise leave a request
// pending for ~30s when the backend stalls, which reads to a player as a frozen
// app rather than a failed save.
const DEFAULT_TIMEOUT_MS = 8000;

// Reads are cached on the device and replayed when the API can't be reached, so
// an outage shows last-known scores instead of an empty page. This is the
// browser-side equivalent of serving stale on origin error.
const CACHE_PREFIX = 'api-cache:';

export async function apiFetch(path, options = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, ...init } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(`${window.API_URL}${path}`, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

export function isJson(res) {
  return (res.headers.get('content-type') || '').includes('application/json');
}

function writeCache(path, data) {
  try {
    localStorage.setItem(
      CACHE_PREFIX + path,
      JSON.stringify({ at: Date.now(), data })
    );
  } catch (e) {
    // Quota or disabled storage. Caching is best-effort and must never break a
    // request that already succeeded.
  }
}

function readCache(path) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + path);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && 'data' in parsed ? parsed : null;
  } catch (e) {
    return null;
  }
}

/**
 * GET that falls back to the last successful response for the same path.
 *
 * Resolves to { data, stale, cachedAt }. `stale: true` means the network failed
 * and this came from the device. Throws only when the network fails and nothing
 * has ever been cached, so callers can still distinguish "no data at all".
 */
export async function cachedGet(path, options = {}) {
  try {
    const res = await apiFetch(path, options);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    writeCache(path, data);
    return { data, stale: false, cachedAt: Date.now() };
  } catch (error) {
    const cached = readCache(path);
    if (cached) return { data: cached.data, stale: true, cachedAt: cached.at };
    throw error;
  }
}

/**
 * Like cachedGet but resolves to `fallback` instead of throwing. Lets a page
 * issue several independent reads where one failure doesn't blank the others.
 */
export async function cachedGetOr(path, fallback, options = {}) {
  try {
    return await cachedGet(path, options);
  } catch (e) {
    return { data: fallback, stale: true, cachedAt: null };
  }
}
